import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { BG_COLORS } from '@/utils/advisorChartUtils'
import type { MemberPrepSession } from '../../redux/types/charterTypes'
import ROUTES from '@/routes/routes'

const ALL_SECTIONS_COUNT = 11

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  not_started: { label: 'Not Started', cls: 'bg-gray-100 text-gray-600' },
  in_progress: { label: 'In Progress', cls: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700' },
}

interface PrepMonitoringCardProps {
  player: MemberPrepSession
  roomId: string
  inviteCode: string
  teamId: number
  colorIndex: number
}

export function PrepMonitoringCard({
  player,
  roomId,
  inviteCode,
  teamId,
  colorIndex,
}: PrepMonitoringCardProps) {
  const navigate = useNavigate()
  const bgColor = BG_COLORS[colorIndex % BG_COLORS.length]
  const completedCount = Object.keys(player.completed_sections).length
  const pct = Math.round((completedCount / ALL_SECTIONS_COUNT) * 100)

  const { label: statusLabel, cls: statusCls } =
    STATUS_BADGE[player.status] ?? STATUS_BADGE.not_started

  const handleViewChat = () => {
    const displayName = encodeURIComponent(
      player.member_name || player.member_email || String(player.member_id)
    )
    const url =
      ROUTES.PROFESSOR.ROOM_CHARTER_PREP_MONITOR(roomId, String(player.member_id)) +
      `?inviteCode=${inviteCode}&teamId=${teamId}&memberName=${displayName}`
    navigate(url)
  }

  return (
    <div
      className={`flex flex-col min-h-[300px] ${bgColor} rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border border-white/10 relative group`}
    >
      <div className="absolute inset-0 bg-[url('/images/create-room-bg.png')] bg-cover bg-center opacity-40 transition-opacity group-hover:opacity-60" />

      {/* Status badge */}
      <div className='absolute z-10 top-3 left-3'>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusCls}`}>
          {statusLabel}
        </span>
      </div>

      {/* Approved badge */}
      {player.profile_approved && (
        <div className='absolute z-10 top-3 right-3'>
          <span className='text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500 text-white'>
            Approved
          </span>
        </div>
      )}

      <div className='z-[1] flex flex-col h-full p-4 pt-14'>
        {/* Name — matches AdvisorChatCard font */}
        <div className='text-center mb-2'>
          <p className='text-sm font-bold text-white leading-tight'>
            {player.member_name || player.member_email}
          </p>
          {player.member_name && (
            <>
              <p className='text-[11px] text-white/60 mt-0.5'>as</p>
              <p className='text-sm font-semibold text-white leading-tight'>
                {player.member_email}
              </p>
            </>
          )}
        </div>

        {/* Progress ring */}
        <div className='flex-1 flex flex-col items-center justify-center gap-2 py-1'>
          <div className='relative w-24 h-24'>
            <svg className='w-24 h-24 -rotate-90' viewBox='0 0 96 96'>
              <circle
                cx='48'
                cy='48'
                r='40'
                fill='none'
                stroke='rgba(255,255,255,0.15)'
                strokeWidth='10'
              />
              <circle
                cx='48'
                cy='48'
                r='40'
                fill='none'
                stroke='white'
                strokeWidth='10'
                strokeDasharray={`${2 * Math.PI * 40}`}
                strokeDashoffset={`${2 * Math.PI * 40 * (1 - pct / 100)}`}
                strokeLinecap='round'
              />
            </svg>
            <div className='absolute inset-0 flex flex-col items-center justify-center'>
              <span className='text-lg font-bold text-white leading-none'>{completedCount}</span>
              <span className='text-[10px] text-white/70'>/{ALL_SECTIONS_COUNT}</span>
            </div>
          </div>
          <p className='text-xs text-white/80 font-medium'>Sections completed</p>
        </div>

        {/* View Chat button — matches AdvisorChatCard style */}
        <Button
          className='w-full max-w-[200px] px-6 py-1.5 h-9 text-sm font-medium rounded-md transition-all mx-auto bg-white text-blue-800 hover:bg-blue-50 shadow-md hover:shadow-lg'
          onClick={handleViewChat}
        >
          View Chat
        </Button>
      </div>
    </div>
  )
}
